import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';

export type TabType = 'actions' | 'filters' | 'columns' | 'notes' | 'documents' | 'copilot';

interface TableFilters {
  [key: string]: any;
}

interface TableSorting {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface PagePanelState {
  isExpanded: boolean;
  isPinned: boolean;
  activeTab: TabType;
}

export interface PendingFilters {
  // Asset filters
  selectedCategories: string[];
  selectedSubCategories?: string[];
  selectedStatuses: string[];
  selectedLocations: string[];
  selectedSubLocations?: string[];
  acquisitionDateRange: { from: Date | undefined; to: Date | undefined };
  acquisitionCostMin: string;
  acquisitionCostMax: string;
  netBookValueMin: string;
  netBookValueMax: string;
  selectedAssignedTo: string[];
  selectedDepreciationMethods: string[];
  warrantyExpiryDateRange: { from: Date | undefined; to: Date | undefined };
  selectedManufacturers: string[];
  
  // Task filters
  selectedTaskTypes?: string[];
  selectedPriorities?: string[];
  dueDateRange?: { from: Date | undefined; to: Date | undefined };
  createdDateRange?: { from: Date | undefined; to: Date | undefined };
  selectedCreatedBy?: string[];
  overdueOnly?: boolean;
  selectedTaskStatuses?: string[];
  selectedTaskPriorities?: string[];
  selectedTaskAssignees?: string[];
  taskDueDateRange?: { from: Date | undefined; to: Date | undefined };
  taskCreatedDateRange?: { from: Date | undefined; to: Date | undefined };
  
  // Audit trail filters
  auditTrailAssetCode?: string;
  auditTrailAssetDescription?: string;
  auditTrailDateRange?: { from: Date | undefined; to: Date | undefined };
  auditTrailActionTypes?: string[];
  auditTrailUsers?: string[];
  
  // Audit filters
  selectedAuditStatuses?: string[];
  selectedAuditTypes?: string[];
  selectedAuditPriorities?: string[];
  selectedAuditors?: string[];
  auditScheduledDateRange?: { from: Date | undefined; to: Date | undefined };
  auditCompletedDateRange?: { from: Date | undefined; to: Date | undefined };
  completionDateRange?: { from: Date | undefined; to: Date | undefined };
  completionMin?: number;
  completionMax?: number;
  completionRateMin?: number;
  completionRateMax?: number;
  totalAssetsMin?: number;
  totalAssetsMax?: number;
  exceptionCountMin?: number;
  exceptionCountMax?: number;
  selectedAssignedUsers?: string[];
  
  // Exception filters
  selectedExceptionTypes?: string[];
  selectedSeverities?: string[];
  selectedAudits?: string[];
  dateReportedRange?: { from: Date | undefined; to: Date | undefined };
  dateResolvedRange?: { from: Date | undefined; to: Date | undefined };
  selectedReportedBy?: string[];
  selectedExceptionSeverities?: string[];
  selectedExceptionStatuses?: string[];
  exceptionDetectedDateRange?: { from: Date | undefined; to: Date | undefined };
  
  // Transaction filters
  selectedTypes?: string[];
  dateRange?: { from: Date | undefined; to: Date | undefined };
  amountMin?: string;
  amountMax?: string;
  selectedFiscalPeriods?: string[];
  selectedTransactionTypes?: string[];
  transactionDateRange?: { from: Date | undefined; to: Date | undefined };
  transactionAmountMin?: number;
  transactionAmountMax?: number;
}

export interface SavedFilter {
  id: string;
  name: string;
  description?: string;
  filters: PendingFilters;
  createdAt: Date;
  updatedAt: Date;
  userId: string;
}

interface RightPanelContextType {
  // Panel state
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  isPinned: boolean;
  setIsPinned: (pinned: boolean) => void;
  
  // Panel dimensions
  panelWidth: number;
  setPanelWidth: (width: number) => void;
  insightsPanelWidth: number;
  setInsightsPanelWidth: (width: number) => void;
  
  // Insights panel
  showInsights: boolean;
  setShowInsights: (show: boolean) => void;
  
  // Row selection
  selectedRowIds: Set<string>;
  setSelectedRowIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  
  // Column management
  visibleColumns: string[];
  setVisibleColumns: (columns: string[]) => void;
  columnOrder: string[];
  setColumnOrder: (order: string[]) => void;
  
  // Filters
  tableFilters: TableFilters;
  setTableFilters: (filters: TableFilters) => void;
  
  // Sorting
  tableSorting: TableSorting;
  setTableSorting: (sorting: TableSorting) => void;
  
  // Active filter count (for badge display)
  activeFilterCount: number;
  setActiveFilterCount: (count: number) => void;
  
  // Pending and Applied Filters
  pendingFilters: PendingFilters | null;
  setPendingFilters: (filters: PendingFilters | null) => void;
  appliedFilters: PendingFilters | null;
  applyFilters: () => void;
  clearPendingFilters: () => void;
  clearAllFilters: () => void;
  
  // Filter loading state
  isApplyingFilters: boolean;
  setIsApplyingFilters: (loading: boolean) => void;
  
  // Saved Filters
  savedFilters: SavedFilter[];
  setSavedFilters: (filters: SavedFilter[]) => void;
  loadSavedFilter: (filterId: string) => void;
  
  // Default/Initial Filters (from deep links)
  defaultFilters: TableFilters;
  setDefaultFilters: (filters: TableFilters) => void;
  
  // Navigation handler (called by NavigationTracker)
  onNavigate: (pathname: string, isBackNavigation?: boolean) => void;
}

const RightPanelContext = createContext<RightPanelContextType | undefined>(undefined);

interface RightPanelProviderProps {
  readonly children: ReactNode;
}

export function RightPanelProvider({ children }: RightPanelProviderProps) {
  // Store panel state per page (keyed by pathname)
  const [pageStates, setPageStates] = useState<Record<string, PagePanelState>>({});
  const [currentPath, setCurrentPath] = useState<string>('');
  
  // Current page state
  const currentPageState = pageStates[currentPath] || {
    isExpanded: false,
    isPinned: false,
    activeTab: 'actions' as TabType,
  };
  
  // Panel state
  const [isExpanded, setIsExpanded] = useState(currentPageState.isExpanded);
  const [activeTab, setActiveTab] = useState<TabType>(currentPageState.activeTab);
  const [isPinned, setIsPinned] = useState(currentPageState.isPinned);
  
  // Panel dimensions
  const [panelWidth, setPanelWidth] = useState(450);
  const [insightsPanelWidth, setInsightsPanelWidth] = useState(350);
  
  // Insights panel
  const [showInsights, setShowInsights] = useState(false);
  
  // Row selection
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  
  // Column management
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  
  // Filters
  const [tableFilters, setTableFilters] = useState<TableFilters>({});
  
  // Sorting
  const [tableSorting, setTableSorting] = useState<TableSorting>({});
  
  // Active filter count
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  
  // Pending and Applied Filters
  const [pendingFilters, setPendingFilters] = useState<PendingFilters | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<PendingFilters | null>(null);
  
  // Filter loading state
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  
  // Saved Filters
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  
  // Default/Initial Filters (from deep links) - used when clearing filters
  const [defaultFilters, setDefaultFilters] = useState<TableFilters>({});
  
  // Store filters, sorting, and column configs per page
  const [pageFilters, setPageFilters] = useState<Record<string, TableFilters>>({});
  const [pageSorting, setPageSorting] = useState<Record<string, TableSorting>>({});
  const [pageSelections, setPageSelections] = useState<Record<string, Set<string>>>({});
  const [pageVisibleColumns, setPageVisibleColumns] = useState<Record<string, string[]>>({});
  const [pageColumnOrder, setPageColumnOrder] = useState<Record<string, string[]>>({});
  
  // Handle navigation: called by NavigationTracker component
  const onNavigate = useCallback((pathname: string, isBackNavigation: boolean = false) => {
    console.log('onNavigate called with pathname:', pathname, 'isBackNavigation:', isBackNavigation);
    
    setCurrentPath(prevPath => {
      // Only reset if we're actually navigating to a different page
      if (pathname === prevPath) {
        console.log('Same path, skipping reset');
        return prevPath;
      }
      
      console.log('Navigating from', prevPath, 'to', pathname);
      
      // Save current page's filters, sorting, selections, and column configs before navigating away
      if (prevPath) {
        setPageFilters(prev => ({
          ...prev,
          [prevPath]: tableFilters
        }));
        setPageSorting(prev => ({
          ...prev,
          [prevPath]: tableSorting
        }));
        setPageSelections(prev => ({
          ...prev,
          [prevPath]: selectedRowIds
        }));
        setPageVisibleColumns(prev => ({
          ...prev,
          [prevPath]: visibleColumns
        }));
        setPageColumnOrder(prev => ({
          ...prev,
          [prevPath]: columnOrder
        }));
      }
      
      const savedState = pageStates[pathname];
      
      if (savedState) {
        // Restore saved state for this page
        setIsExpanded(savedState.isExpanded);
        setActiveTab(savedState.activeTab);
        setIsPinned(savedState.isPinned);
      } else {
        // New page: collapse panel unless it was pinned on previous page
        setIsExpanded(false);
        setActiveTab('actions');
        setIsPinned(false);
      }
      
      // Restore filters, sorting, selections, and column configs if navigating back, otherwise reset
      if (isBackNavigation && pageFilters[pathname]) {
        console.log('Restoring filters and sorting for back navigation:', pageFilters[pathname], pageSorting[pathname]);
        setTableFilters(pageFilters[pathname]);
        setTableSorting(pageSorting[pathname] || {});
        setSelectedRowIds(pageSelections[pathname] || new Set());
        setVisibleColumns(pageVisibleColumns[pathname] || []);
        setColumnOrder(pageColumnOrder[pathname] || []);
      } else {
        // Reset filters and sorting on forward navigation
        // Individual pages will handle deep-link filters via URL params
        console.log('Resetting filters and sorting due to forward navigation');
        setTableFilters({});
        setTableSorting({});
        setSelectedRowIds(new Set());
        // Restore column configs if they exist for this page, otherwise reset
        setVisibleColumns(pageVisibleColumns[pathname] || []);
        setColumnOrder(pageColumnOrder[pathname] || []);
      }
      
      return pathname;
    });
  }, [pageStates, tableFilters, tableSorting, selectedRowIds, visibleColumns, columnOrder, pageFilters, pageSorting, pageSelections, pageVisibleColumns, pageColumnOrder]);
  
  // Wrapper functions to save state when changed
  const setIsExpandedAndStore = useCallback((expanded: boolean) => {
    setIsExpanded(expanded);
    if (currentPath) {
      setPageStates(prev => ({
        ...prev,
        [currentPath]: {
          ...prev[currentPath] || { isPinned: false, activeTab: 'actions' as TabType },
          isExpanded: expanded,
        },
      }));
    }
  }, [currentPath]);
  
  const setActiveTabAndStore = useCallback((tab: TabType) => {
    setActiveTab(tab);
    if (currentPath) {
      setPageStates(prev => ({
        ...prev,
        [currentPath]: {
          ...prev[currentPath] || { isPinned: false, isExpanded: false },
          activeTab: tab,
        },
      }));
    }
  }, [currentPath]);
  
  const setIsPinnedAndStore = useCallback((pinned: boolean) => {
    setIsPinned(pinned);
    if (currentPath) {
      setPageStates(prev => ({
        ...prev,
        [currentPath]: {
          ...prev[currentPath] || { activeTab: 'actions' as TabType, isExpanded: false },
          isPinned: pinned,
          isExpanded: pinned, // If pinned, keep expanded
        },
      }));
    }
  }, [currentPath]);
  
  // Apply pending filters - converts PendingFilters to TableFilters format
  const applyFilters = useCallback(() => {
    if (pendingFilters) {
      // Set loading state to show placeholder
      setIsApplyingFilters(true);
      
      // Use setTimeout to ensure the loading state is rendered before processing
      setTimeout(() => {
        setAppliedFilters(pendingFilters);
        
        // Convert PendingFilters to TableFilters format for the grid
        const newTableFilters: TableFilters = {};
        
        if (pendingFilters.selectedCategories.length > 0) {
          newTableFilters.category = pendingFilters.selectedCategories;
        }
        if (pendingFilters.selectedStatuses.length > 0) {
          newTableFilters.status = pendingFilters.selectedStatuses;
        }
        if (pendingFilters.selectedLocations.length > 0) {
          newTableFilters.location = pendingFilters.selectedLocations;
        }
        if (pendingFilters.acquisitionDateRange.from || pendingFilters.acquisitionDateRange.to) {
          newTableFilters.acquisitionDateRange = pendingFilters.acquisitionDateRange;
        }
        if (pendingFilters.acquisitionCostMin) {
          newTableFilters.acquisitionCostMin = pendingFilters.acquisitionCostMin;
        }
        if (pendingFilters.acquisitionCostMax) {
          newTableFilters.acquisitionCostMax = pendingFilters.acquisitionCostMax;
        }
        if (pendingFilters.netBookValueMin) {
          newTableFilters.netBookValueMin = pendingFilters.netBookValueMin;
        }
        if (pendingFilters.netBookValueMax) {
          newTableFilters.netBookValueMax = pendingFilters.netBookValueMax;
        }
        if (pendingFilters.selectedAssignedTo.length > 0) {
          newTableFilters.assignedTo = pendingFilters.selectedAssignedTo;
        }
        if (pendingFilters.selectedDepreciationMethods.length > 0) {
          newTableFilters.depreciationMethod = pendingFilters.selectedDepreciationMethods;
        }
        if (pendingFilters.warrantyExpiryDateRange.from || pendingFilters.warrantyExpiryDateRange.to) {
          newTableFilters.warrantyExpiryDateRange = pendingFilters.warrantyExpiryDateRange;
        }
        if (pendingFilters.selectedManufacturers.length > 0) {
          newTableFilters.manufacturer = pendingFilters.selectedManufacturers;
        }
        
        setTableFilters(newTableFilters);
        
        // Reset loading state after a brief delay to allow filtered data to render
        setTimeout(() => {
          setIsApplyingFilters(false);
        }, 300);
      }, 0);
    }
  }, [pendingFilters, setTableFilters]);
  
  // Clear pending filters
  const clearPendingFilters = useCallback(() => {
    setPendingFilters(null);
  }, []);
  
  // Clear all filters (both pending and applied) - resets to default filters from deep links
  const clearAllFilters = useCallback(() => {
    setPendingFilters(null);
    setAppliedFilters(null);
    setActiveFilterCount(0);
    setTableFilters(defaultFilters);
  }, [defaultFilters]);
  
  // Load a saved filter
  const loadSavedFilter = useCallback((filterId: string) => {
    const filter = savedFilters.find(f => f.id === filterId);
    if (filter) {
      setPendingFilters(filter.filters);
    }
  }, [savedFilters]);

  const value: RightPanelContextType = useMemo(() => ({
    isExpanded,
    setIsExpanded: setIsExpandedAndStore,
    activeTab,
    setActiveTab: setActiveTabAndStore,
    isPinned,
    setIsPinned: setIsPinnedAndStore,
    panelWidth,
    setPanelWidth,
    insightsPanelWidth,
    setInsightsPanelWidth,
    showInsights,
    setShowInsights,
    selectedRowIds,
    setSelectedRowIds,
    visibleColumns,
    setVisibleColumns,
    columnOrder,
    setColumnOrder,
    tableFilters,
    setTableFilters,
    tableSorting,
    setTableSorting,
    activeFilterCount,
    setActiveFilterCount,
    pendingFilters,
    setPendingFilters,
    appliedFilters,
    applyFilters,
    clearPendingFilters,
    clearAllFilters,
    isApplyingFilters,
    setIsApplyingFilters,
    savedFilters,
    setSavedFilters,
    loadSavedFilter,
    defaultFilters,
    setDefaultFilters,
    onNavigate,
  }), [
    isExpanded,
    setIsExpandedAndStore,
    activeTab,
    setActiveTabAndStore,
    isPinned,
    setIsPinnedAndStore,
    panelWidth,
    insightsPanelWidth,
    showInsights,
    selectedRowIds,
    visibleColumns,
    columnOrder,
    tableFilters,
    tableSorting,
    activeFilterCount,
    pendingFilters,
    appliedFilters,
    applyFilters,
    clearPendingFilters,
    clearAllFilters,
    isApplyingFilters,
    savedFilters,
    loadSavedFilter,
    defaultFilters,
    onNavigate,
  ]);

  return (
    <RightPanelContext.Provider value={value}>
      {children}
    </RightPanelContext.Provider>
  );
}

export function useRightPanel() {
  const context = useContext(RightPanelContext);
  if (context === undefined) {
    throw new Error('useRightPanel must be used within a RightPanelProvider');
  }
  return context;
}
